import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ToastService, ToastMessage } from '../../../core/services/toast.service';

/**
 * Toast Container Component
 *
 * Renders toast notifications in a fixed position at the bottom-right of the viewport.
 * Subscribes to ToastService events and supports stacking multiple toasts.
 * Each toast auto-dismisses after its configured duration.
 */
@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-container position-fixed bottom-0 end-0 p-3" data-bs-theme="dark" style="z-index: 1090;">
      <div
        *ngFor="let toast of toasts; trackBy: trackById"
        class="toast show"
        [ngClass]="getHeaderClass(toast.type)"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="toast-header">
          <i class="bi me-2" [ngClass]="getIconClass(toast.type)"></i>
          <strong class="me-auto">{{ getTitle(toast.type) }}</strong>
          <button
            type="button"
            class="btn-close btn-close-white"
            aria-label="Close"
            (click)="removeToast(toast.id)"
          ></button>
        </div>
        <div class="toast-body">
          {{ toast.message }}
        </div>
      </div>
    </div>
  `,
})
export class ToastContainerComponent implements OnInit, OnDestroy {
  toasts: ToastMessage[] = [];
  private subscription: Subscription | null = null;
  private timers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.subscription = this.toastService.toasts$.subscribe((toast) => {
      this.toasts.push(toast);
      this.scheduleRemoval(toast);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
  }

  /**
   * Schedule auto-removal of a toast after its duration
   */
  private scheduleRemoval(toast: ToastMessage): void {
    const timer = setTimeout(() => {
      this.removeToast(toast.id);
    }, toast.duration);
    this.timers.set(toast.id, timer);
  }

  /**
   * Remove a toast by ID
   */
  removeToast(id: string): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  /**
   * Track toasts by ID for ngFor performance
   */
  trackById(_index: number, toast: ToastMessage): string {
    return toast.id;
  }

  /**
   * Get CSS class for toast header based on type
   */
  getHeaderClass(type: string): string {
    switch (type) {
      case 'success': return 'toast-success';
      case 'error': return 'toast-error';
      case 'info': return 'toast-info';
      default: return 'toast-info';
    }
  }

  /**
   * Get icon class for toast type
   */
  getIconClass(type: string): string {
    switch (type) {
      case 'success': return 'bi-check-circle-fill';
      case 'error': return 'bi-exclamation-triangle-fill';
      case 'info': return 'bi-info-circle-fill';
      default: return 'bi-info-circle-fill';
    }
  }

  /**
   * Get title text for toast type
   */
  getTitle(type: string): string {
    switch (type) {
      case 'success': return 'Success';
      case 'error': return 'Error';
      case 'info': return 'Info';
      default: return 'Notification';
    }
  }
}
